//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import {
  AssetTypes,
  FileUploaderAssetFailure,
} from '@campaigns/components/shared/file-uploader/FileUploaderTypes';
import { SupportedMimeTypes } from '@utils/mimeTypes';
import { convertBytesToReadableSize, pluralize } from '@utils/stringUtils';

/**
 * Generates a descriptive text for upload campaign assets based on a prefix, file category, and whether multiple files are accepted.
 *
 * @param prefix - The prefix text to be used in the generated text.
 * @param allowedFileCategories - An array of allowed file categories.
 * @param shouldAcceptMultipleFiles - A flag indicating if multiple files are accepted.
 * @returns - The generated text for upload campaign assets.
 *
 * @example
 * // If allowedFileCategories has one category and multiple files are accepted
 * generateTextForUploadCampaignAssets('Upload', ['image'], true);
 * // Returns: "Upload images"
 *
 * @example
 * // If allowedFileCategories has multiple categories and single file is accepted
 * generateTextForUploadCampaignAssets('Upload', ['image', 'video'], false);
 * // Returns: "Upload file"
 */
export const generateTextForUploadCampaignAssets = (
  prefix: string,
  allowedFileCategories: AssetTypes[],
  shouldAcceptMultipleFiles: boolean,
) => {
  const assetType = allowedFileCategories[0];
  if (allowedFileCategories.length === 1 && assetType) {
    return `${prefix} ${pluralize(shouldAcceptMultipleFiles ? 2 : 1, assetType, undefined, false)}`;
  }
  return `${prefix} ${pluralize(shouldAcceptMultipleFiles ? 2 : 1, 'file', 'files', false)}`;
};

/**
 * Generates an error message for upload campaign assets based on the provided failure conditions.
 *
 * @param failure - An object containing failure flags.
 * @param maxTotalSizeOfAssets - Maximum allowed total size of the assets in bytes.(Optional)
 * @param shouldAcceptMultipleFiles - Whether multiple files are allowed. (Optional)
 * @param maxNumberOfAssets - Maximum number of assets allowed. (Optional)
 * @returns The generated error message.
 */
export const generateErrorTextForUploadCampaignAssets = (
  {
    isCountTooMany,
    isSizeTooLarge,
    isUnsupportedFilePresent,
    unknownReason,
  }: FileUploaderAssetFailure,
  maxTotalSizeOfAssets?: number,
  shouldAcceptMultipleFiles?: boolean,
  maxNumberOfAssets?: number,
): string[] => {
  const errors: string[] = [];
  if (isCountTooMany) {
    if (shouldAcceptMultipleFiles && maxNumberOfAssets) {
      errors.push(`Only allowed to upload up to ${maxNumberOfAssets} files.`);
    } else {
      errors.push(`Only allowed to upload 1 file.`);
    }
  }
  if (isSizeTooLarge && maxTotalSizeOfAssets) {
    const sizeText = convertBytesToReadableSize(maxTotalSizeOfAssets);
    errors.push(`Only allowed to upload up to ${sizeText}.`);
  }
  if (isUnsupportedFilePresent) {
    errors.push(`Unsupported file detected.`);
  }
  if (unknownReason) {
    errors.push('Failure while uploading.');
  }
  return errors;
};

/**
 * Checks if the given MIME type is supported.
 *
 * @param type - The MIME type to check.
 * @param  allowedMimeTypes - An array of allowed MIME types.
 * @returns Whether the MIME type is supported.
 */
export const isSupportedMimeType = (
  type: string,
  allowedMimeTypes: SupportedMimeTypes[],
): type is SupportedMimeTypes => {
  return allowedMimeTypes.includes(type as SupportedMimeTypes);
};

/**
 * Formats a file name by truncating it to a specified length and adding an ellipsis if necessary.
 *
 * @param fileName - The name of the file to format.
 * @param characterCutoffPoint - The maximum number of characters allowed before truncation.
 * @returns - The formatted file name. If the original file name length is less than or equal to the cutoff point, it returns the original file name. Otherwise, it returns the truncated file name with an ellipsis.
 */
export const formatFileName = (
  fileName: string,
  characterCutoffPoint: number,
): string => {
  if (fileName.length <= characterCutoffPoint) {
    return fileName;
  }

  return fileName.slice(0, characterCutoffPoint) + '...';
};

// TODO: PD-252890 - Support File Append to Existing Uploads with API Validation Checks
export const getFileSizeAndType = async (
  url: string,
): Promise<{ size: number; type: string }> => {
  try {
    const response = await fetch(url, {
      method: 'HEAD',
      redirect: 'follow',
      cache: 'reload',
    });

    const contentLength = response.headers.get('Content-Length');
    const contentType = response.headers.get('Content-Type');

    return {
      size: contentLength ? +contentLength : 0,
      type: contentType ?? 'unsupported',
    };
  } catch (error) {
    console.error('Error while fetching file size and type:', error);
    return { size: 0, type: 'unsupported' };
  }
};

/**
 * Sanitizes the filename but also retains the file extension.
 * Removes special characters, keeps letters, numbers, and underscores.
 *
 * @param filename - The original filename to be sanitized.
 * @returns The sanitized filename with the original extension retained.
 */
export const sanitizeFileName = (filename: string): string => {
  const extensionIndex = filename.lastIndexOf('.');
  const baseName =
    extensionIndex === -1 ? filename : filename.slice(0, extensionIndex);
  const extension = extensionIndex === -1 ? '' : filename.slice(extensionIndex);

  const sanitizedBaseName = baseName
    .replace(/[^\w\s]+/g, '')
    .replace(/\s+/g, '_');

  return `${sanitizedBaseName}${extension}`;
};

export const getSanitizedFiles = (files: File[]): File[] => {
  return files.map((file) => {
    const sanitizedFileName = sanitizeFileName(file.name);
    return new File([file], sanitizedFileName, { type: file.type });
  });
};
